import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Select from "react-select";
import validator from "validator";
import axios from "axios";
import BusinessCategorySelect from "./BusinessCategorySelect";
import {
  FaArrowRight,
  FaCheckCircle,
  FaImage,
  FaPlus,
  FaQuestionCircle,
  FaTag,
  FaTimes,
} from "react-icons/fa";
import Header from "./Header";
import Footer from "./Footer";
import "./PartnerSignUp.css";
import {
  COMMUNITY_SELECT_OPTIONS,
} from "./communityDirectoryConfig";
const API_BASE = (
  process.env.REACT_APP_GOOURHUB_BUSINESS_API ||
  "https://business-api.goourhub.com"
).replace(/\/+$/, "");

const MAX_BUSINESS_PHOTOS = 4;
const MAX_BUSINESS_PHOTO_BYTES = 5 * 1024 * 1024;
const ALLOWED_BUSINESS_PHOTO_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
]);

const businessPhotoValidationMessage = (file) => {
  if (!ALLOWED_BUSINESS_PHOTO_TYPES.has(file?.type)) {
    return `${file?.name || "Photo"}: use JPG, PNG, or WEBP.`;
  }

  if (file.size > MAX_BUSINESS_PHOTO_BYTES) {
    return `${file.name}: each photo must be 5 MB or smaller.`;
  }

  return "";
};

const AWS_REGISTRATION_ENDPOINT =
  "https://mznser7yt4.execute-api.us-east-2.amazonaws.com/test/gouourhub/registration";

// The header/account gate may persist the verified account under a different
// storage key. Rather than creating a second login state, restore any saved
// account object that has BOTH the signed-in email and the reusable
// verification token required by /api/partners/register.
const AUTH_STORAGE_KEYS = [
  "goourhub_verified_account",
  "goourhubVerifiedAccount",
  "goourhub_auth",
  "goourhubAuth",
  "goourhub_session",
  "goourhubSession",
  "verifiedAccount",
  "authUser",
  "currentUser",
];

const normalizeVerifiedAccount = (candidate) => {
  if (!candidate || typeof candidate !== "object") {
    return null;
  }

  const nestedObjects = [
    candidate,
    candidate.verifiedAccount,
    candidate.account,
    candidate.user,
    candidate.authUser,
    candidate.session,
  ].filter((item) => item && typeof item === "object");

  for (const nested of nestedObjects) {
    const source = { ...candidate, ...nested };

    const email = String(
      source.email || source.userEmail || source.user_id || ""
    )
      .trim()
      .toLowerCase();

  const verificationToken = String(
  source.verificationToken ||
    source.verification_token ||
    ""
).trim();

const sessionToken = String(
  source.sessionToken ||
    source.session_token ||
    source.token ||
    ""
).trim();

if (
  !email ||
  (!verificationToken && !sessionToken)
) {
  continue;
}

    return {
      ...source,
      email,
      verificationToken,
      sessionToken,
      firstName: String(
        source.firstName || source.first_name || source.givenName || ""
      ).trim(),
      lastName: String(
        source.lastName || source.last_name || source.familyName || ""
      ).trim(),
      accountExists:
        source.accountExists ?? source.account_exists ?? true,
    };
  }

  return null;
};

const readStoredVerifiedAccount = () => {
  if (typeof window === "undefined") {
    return null;
  }

  const storages = [window.sessionStorage, window.localStorage];

  for (const storage of storages) {
    if (!storage) continue;

    const keys = [
      ...AUTH_STORAGE_KEYS,
      ...Array.from({ length: storage.length }, (_, index) => storage.key(index)),
    ].filter(Boolean);

    for (const key of [...new Set(keys)]) {
      try {
        const rawValue = storage.getItem(key);
        if (!rawValue) continue;

        const parsed = JSON.parse(rawValue);
        const account = normalizeVerifiedAccount(parsed);

        if (account) {
          return account;
        }
      } catch {
        // Ignore non-JSON or inaccessible storage entries.
      }
    }
  }

  return null;
};

function PartnerSignUpPage({
  verifiedAccount = null,
}) {
  const features = [
    { name: "Branded webpage included", icon: "🎁" },
    { name: "No ongoing platform fees", icon: "💰" },
    { name: "Complimentary business promotion", icon: "📢" },
  ];

  const communityOptions = COMMUNITY_SELECT_OPTIONS;

  const languageOptions = [
   "English", "Amharic", "Arabic", "Bengali", "Chinese", "Ethiopian", "Filipino (Tagalog)",
    "French", "Gujarati", "German", "Hebrew", "Hindi", "Igbo", "Italian",
    "Japanese", "Korean", "Mandarin", "Oromo", "Portuguese", "Spanish",
    "Swahili", "Tigrinya", "Others",
  ].map((l) => ({ value: l.toLowerCase(), label: l }));

  // 🔐 Auto-generate random password (10 characters)
  const generatePassword = () => {
    const charset =
      "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    return Array.from(
      { length: 10 },
      () => charset[Math.floor(Math.random() * charset.length)]
    ).join("");
  };

  const [formData, setFormData] = useState({
    user_id: "",
    password: "",
    email: "",
    first_name: "",
    last_name: "",
    bus_name: "",
    street_address: "",
    city: "San Antonio",
    state: "Texas",
    user_type: "vendor",
    requester_role: "owner", // NEW just identifies who requested (owner or team member)
    ph_no: "",
    isAccepted: false,
    business_type: "",
    other_business_type: "",
    communities: [],
    other_community: "",
    languages: [],
    other_language: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  // Vendor email verification is handled by the DigitalOcean business API.
  const [verificationStage, setVerificationStage] = useState("idle");
  const [challengeId, setChallengeId] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [verificationToken, setVerificationToken] = useState("");
  const [sessionToken, setSessionToken] = useState("");
  const [accountExists, setAccountExists] = useState(false);
  const [isVerifyingEmail, setIsVerifyingEmail] = useState(false);

  // "premium" -> Stripe checkout after registration (current flow)
  // "free"    -> skip Stripe, go straight to vendor thank-you
  const [listingPlan, setListingPlan] = useState("premium");

  // Optional business photos. The first selected image becomes the cover.
  const [businessPhotos, setBusinessPhotos] = useState([]);
  const [photoWarning, setPhotoWarning] = useState("");
  const [isUploadingPhotos, setIsUploadingPhotos] = useState(false);
  const businessPhotoInputRef = useRef(null);
  const businessPhotoPreviewUrlsRef = useRef(new Set());

  // Prefer the account explicitly supplied by AccountGate. When this page is
  // reached directly from the header, fall back to the same persisted browser
  // session so the visitor is not forced through email/OTP again.
  const normalizedPropAccount = useMemo(
    () => normalizeVerifiedAccount(verifiedAccount),
    [verifiedAccount]
  );

  const [storedVerifiedAccount, setStoredVerifiedAccount] = useState(() =>
    readStoredVerifiedAccount()
  );

  const activeVerifiedAccount =
    normalizedPropAccount || storedVerifiedAccount || null;

const hasReusableVerification = Boolean(
  activeVerifiedAccount?.email &&
    (
      activeVerifiedAccount?.verificationToken ||
      activeVerifiedAccount?.sessionToken
    )
);

  useEffect(() => {
    const refreshStoredAccount = () => {
      setStoredVerifiedAccount(readStoredVerifiedAccount());
    };

    refreshStoredAccount();
    window.addEventListener("storage", refreshStoredAccount);
    window.addEventListener("focus", refreshStoredAccount);
    window.addEventListener("goourhub-auth-changed", refreshStoredAccount);

    return () => {
      window.removeEventListener("storage", refreshStoredAccount);
      window.removeEventListener("focus", refreshStoredAccount);
      window.removeEventListener("goourhub-auth-changed", refreshStoredAccount);
    };
  }, []);

  useEffect(() => {
    if (!activeVerifiedAccount?.email) {
      return;
    }

    const verifiedEmail = activeVerifiedAccount.email.trim().toLowerCase();

    setFormData((currentFormData) => ({
      ...currentFormData,
      email: verifiedEmail,
      user_id: verifiedEmail,
      first_name:
        activeVerifiedAccount.firstName || currentFormData.first_name,
      last_name:
        activeVerifiedAccount.lastName || currentFormData.last_name,
    }));

    setAccountExists(Boolean(activeVerifiedAccount.accountExists));

   if (activeVerifiedAccount.verificationToken) {
  setVerificationToken(
    activeVerifiedAccount.verificationToken
  );
}

if (activeVerifiedAccount.sessionToken) {
  setSessionToken(
    activeVerifiedAccount.sessionToken
  );
}

if (
  activeVerifiedAccount.verificationToken ||
  activeVerifiedAccount.sessionToken
) {
  setVerificationStage("verified");
  setChallengeId("");
  setVerificationCode("");
}
  }, [activeVerifiedAccount]);

  useEffect(() => {
    return () => {
      businessPhotoPreviewUrlsRef.current.forEach((url) => {
        URL.revokeObjectURL(url);
      });
      businessPhotoPreviewUrlsRef.current.clear();
    };
  }, []);

  const phoneRef = useRef(null);
  const emailRef = useRef(null);

  const validateEmail = (email) => validator.isEmail(email);
  const validatePhone = (phone) => {
    const digitsOnly = phone.replace(/\D/g, "");
    return digitsOnly.length === 10;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => {
      const newForm = {
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      };

      // Keep the legacy regular-user ID synchronized with email.
      if (name === "email") {
        newForm.user_id = value;
      }

      return newForm;
    });

    if (name === "email") {
      setVerificationStage("idle");
      setChallengeId("");
      setVerificationCode("");
      setVerificationToken("");
      setSessionToken("");
      setAccountExists(false);
      setSuccessMessage("");
    }
  };

  // For react-select multi-select fields
  const handleMultiChange = (selected, name) => {
    setFormData((prev) => ({
      ...prev,
      [name]: selected || [],
    }));
  };

  // For text inputs within multi-select groups (Others textboxes)
  const handleTextChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const openBusinessPhotoPicker = () => {
    if (
      isSubmitting ||
      businessPhotos.length >= MAX_BUSINESS_PHOTOS
    ) {
      return;
    }

    businessPhotoInputRef.current?.click();
  };

  const handleBusinessPhotoSelection = (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    event.target.value = "";

    if (!selectedFiles.length) {
      return;
    }

    setPhotoWarning("");

    const availableSlots =
      MAX_BUSINESS_PHOTOS - businessPhotos.length;

    if (availableSlots <= 0) {
      setPhotoWarning(
        "You can upload a maximum of 4 business photos."
      );
      return;
    }

    const accepted = [];
    const validationMessages = [];

    for (const file of selectedFiles) {
      const validationMessage =
        businessPhotoValidationMessage(file);

      if (validationMessage) {
        validationMessages.push(validationMessage);
        continue;
      }

      if (accepted.length >= availableSlots) {
        validationMessages.push(
          "Only the available photo slots were added. The maximum is 4."
        );
        continue;
      }

      const previewUrl = URL.createObjectURL(file);
      businessPhotoPreviewUrlsRef.current.add(previewUrl);

      accepted.push({
        id: `${file.name}-${file.size}-${file.lastModified}-${Math.random()}`,
        file,
        previewUrl,
      });
    }

    if (accepted.length) {
      setBusinessPhotos((current) => [
        ...current,
        ...accepted,
      ]);
    }

    if (validationMessages.length) {
      setPhotoWarning(
        [...new Set(validationMessages)].join(" ")
      );
    }
  };

  const removeBusinessPhoto = (photoId) => {
    setBusinessPhotos((current) => {
      const removed = current.find(
        (photo) => photo.id === photoId
      );

      if (removed) {
        URL.revokeObjectURL(removed.previewUrl);
        businessPhotoPreviewUrlsRef.current.delete(
          removed.previewUrl
        );
      }

      return current.filter(
        (photo) => photo.id !== photoId
      );
    });

    setPhotoWarning("");
  };

  const clearBusinessPhotos = () => {
    businessPhotos.forEach((photo) => {
      URL.revokeObjectURL(photo.previewUrl);
      businessPhotoPreviewUrlsRef.current.delete(
        photo.previewUrl
      );
    });

    setBusinessPhotos([]);
    setPhotoWarning("");

    if (businessPhotoInputRef.current) {
      businessPhotoInputRef.current.value = "";
    }
  };

  const uploadBusinessPhotos = async (
    businessId,
    confirmationNumber
  ) => {
    if (!businessPhotos.length) {
      return {
        uploadedCount: 0,
        failedCount: 0,
      };
    }

    if (!businessId || !confirmationNumber) {
      return {
        uploadedCount: 0,
        failedCount: businessPhotos.length,
      };
    }

    let uploadedCount = 0;
    let failedCount = 0;

    setIsUploadingPhotos(true);

    try {
      for (const photo of businessPhotos) {
        try {
          await axios.post(
            `${API_BASE}/api/businesses/${encodeURIComponent(
              businessId
            )}/photos`,
            photo.file,
            {
              headers: {
                "Content-Type": photo.file.type,
                "X-Business-Confirmation":
                  confirmationNumber,
              },
            }
          );

          uploadedCount += 1;
        } catch (error) {
          failedCount += 1;
          console.error(
            `Business photo upload failed for ${photo.file.name}:`,
            error
          );
        }
      }
    } finally {
      setIsUploadingPhotos(false);
    }

    return {
      uploadedCount,
      failedCount,
    };
  };

  const MyErrorModal = ({ message, onClose }) => (
    <div className="ps-modal-overlay" role="alertdialog" aria-modal="true">
      <div className="ps-modal">
        <button
          type="button"
          className="ps-modal__close"
          onClick={onClose}
          aria-label="Close"
        >
          &times;
        </button>
        <p className="ps-modal__message">{message}</p>
        <button type="button" className="ps-btn ps-btn--ghost" onClick={onClose}>
          Got it
        </button>
      </div>
    </div>
  );

  const getApiErrorMessage = (error, fallback) => {
    const data = error?.response?.data;
    return (
      data?.message ||
      data?.body?.message ||
      (typeof data === "string" ? data : "") ||
      error?.message ||
      fallback
    );
  };

  const sendVerificationCode = async () => {
    setErrorMessage("");
    setSuccessMessage("");

    const email = formData.email.trim().toLowerCase();
    if (!validateEmail(email)) {
      setErrorMessage("Please enter a valid email address first.");
      emailRef.current?.scrollIntoView({ behavior: "smooth" });
      return;
    }

    setIsVerifyingEmail(true);
    try {
      const response = await axios.post(`${API_BASE}/api/auth/email/start`, {
        email,
        purpose: "partner-signup",
      });

      setChallengeId(response.data.challengeId);
      setVerificationCode("");
      setVerificationToken("");
      setAccountExists(false);
      setVerificationStage("code-sent");
      setSuccessMessage(
        "A 6-digit verification code was sent to your email."
      );
    } catch (error) {
      setErrorMessage(
        getApiErrorMessage(
          error,
          "We could not send the verification code. Please try again."
        )
      );
    } finally {
      setIsVerifyingEmail(false);
    }
  };

  const verifyEmailCode = async () => {
    setErrorMessage("");
    setSuccessMessage("");

    const code = verificationCode.replace(/\D/g, "");
    if (!challengeId || code.length !== 6) {
      setErrorMessage("Enter the 6-digit verification code.");
      return;
    }

    setIsVerifyingEmail(true);
    try {
      const response = await axios.post(`${API_BASE}/api/auth/email/verify`, {
        challengeId,
        code,
      });

      setVerificationToken(response.data.verificationToken);
      setSessionToken(
  response.data.sessionToken || ""
);
      setAccountExists(Boolean(response.data.accountExists));
      setVerificationStage("verified");
      setSuccessMessage(
        response.data.accountExists
          ? "Email verified. Welcome back you may add another business."
          : "Email verified. You may continue with registration."
      );
    } catch (error) {
      setErrorMessage(
        getApiErrorMessage(
          error,
          "The verification code could not be verified."
        )
      );
    } finally {
      setIsVerifyingEmail(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrorMessage("");
    setSuccessMessage("");
    setIsSubmitting(true);

    const {
      email,
      first_name,
      last_name,
      city,
      state,
      user_type,
      ph_no,
      isAccepted,
      business_type,
      other_business_type,
      communities,
      other_community,
      languages,
      other_language,
      street_address,
    } = formData;

    if (
      !email ||
      !first_name ||
      !last_name ||
      !city ||
      !state ||
      !user_type ||
      !ph_no
    ) {
      setErrorMessage("Please fill in all required fields.");
      setIsSubmitting(false);
      return;
    }

    if (!validateEmail(email)) {
      setErrorMessage("Please enter a valid email address.");
      setIsSubmitting(false);
      emailRef.current?.scrollIntoView({ behavior: "smooth" });
      return;
    }

    if (!validatePhone(ph_no)) {
      setErrorMessage("Please enter a valid 10-digit phone number.");
      setIsSubmitting(false);
      phoneRef.current?.scrollIntoView({ behavior: "smooth" });
      return;
    }

    if (!isAccepted) {
      setErrorMessage("Please accept the terms before proceeding.");
      setIsSubmitting(false);
      return;
    }

    try {
      if (user_type === "vendor") {
      const hasVerifiedAccess = Boolean(
  verificationToken || sessionToken
);

if (
  verificationStage !== "verified" ||
  !hasVerifiedAccess
) {
  setErrorMessage(
    "Verify your email before submitting the business."
  );
  setIsSubmitting(false);
  return;
}

        if (!formData.bus_name.trim()) {
          setErrorMessage("Business name is required for vendors.");
          setIsSubmitting(false);
          return;
        }
        if (!business_type) {
          setErrorMessage("Please select your business type.");
          setIsSubmitting(false);
          return;
        }
        if (!street_address.trim()) {
          setErrorMessage("Street address is required.");
          setIsSubmitting(false);
          return;
        }
        if (city !== "San Antonio" || state !== "Texas") {
          setErrorMessage(
            "Partner listings are currently limited to San Antonio, Texas."
          );
          setIsSubmitting(false);
          return;
        }
        if (
          (business_type === "others" || business_type === "multiple") &&
          !other_business_type.trim()
        ) {
          setErrorMessage("Please specify your business type.");
          setIsSubmitting(false);
          return;
        }
        if (
          communities.some((item) => item.value === "Others") &&
          !other_community.trim()
        ) {
          setErrorMessage(
            "Please specify the community in which your business focuses."
          );
          setIsSubmitting(false);
          return;
        }
        if (
          languages.some((item) => item.value === "others") &&
          !other_language.trim()
        ) {
          setErrorMessage("Please specify the additional language(s) you offer.");
          setIsSubmitting(false);
          return;
        }

        const address = `${street_address.trim()}, San Antonio, Texas`;
        const response = await axios.post(
  `${API_BASE}/api/partners/register`,
  {
    verificationToken,
    email: email.trim().toLowerCase(),
    first_name: first_name.trim(),
    last_name: last_name.trim(),
    ownerPhoneNumber: ph_no.trim(),
    name: formData.bus_name.trim(),
    phoneNumber: ph_no.trim(),
    business_type,
    category: business_type,
    other_business_type:
      other_business_type.trim(),
    street_address:
      street_address.trim(),
    address,
    city: "San Antonio",
    state: "Texas",
    communities:
      communities.map(
        (item) => item.value
      ),
    other_community:
      other_community.trim(),
    languages:
      languages.map(
        (item) => item.value
      ),
    other_language:
      other_language.trim(),
    listing_plan: listingPlan,
    source: "partner-signup",
    requester_role:
      formData.requester_role,
    termsAccepted: true,
  },
  sessionToken
    ? {
        headers: {
          Authorization:
            `Bearer ${sessionToken}`,
        },
      }
    : undefined
);

        const result = response.data;

        const photoUploadResult =
          await uploadBusinessPhotos(
            result.businessId,
            result.confirmationNumber
          );

        const signUpData = {
          accountId: result.accountId,
          businessId: result.businessId,
          confirmationNumber: result.confirmationNumber,
          listingPlan: result.listingPlan,
          paymentStatus: result.paymentStatus,
          accountExists: result.accountExists,
          first_name,
          email,
          photoCount: photoUploadResult.uploadedCount,
          photoUploadFailed:
            photoUploadResult.failedCount,
        };

        localStorage.setItem(
          "pendingVendorSignup",
          JSON.stringify(signUpData)
        );

        clearBusinessPhotos();

        if (listingPlan === "premium") {
          if (!result.checkoutUrl) {
            throw new Error(
              "Registration was saved, but secure checkout is unavailable."
            );
          }
          window.location.assign(result.checkoutUrl);
          return;
        }

        window.location.assign(
          `/vendor-thankyou?firstName=${encodeURIComponent(
            first_name
          )}&plan=free&confirmation=${encodeURIComponent(
            result.confirmationNumber || ""
          )}`
        );
        return;
      }

      // Keep the existing regular-user registration flow for now.
      const user_id = email.trim();
      const password = generatePassword();
      const response = await axios.post(AWS_REGISTRATION_ENDPOINT, {
        ...formData,
        user_id,
        password,
        user_type: "U",
        listing_plan: "",
      });

      if (response.status !== 200) {
        throw new Error("Unexpected server response. Please try again.");
      }

      const signUpData = {
        user_id,
        user_type,
        first_name,
        email,
      };
      localStorage.setItem("pendingUserSignup", JSON.stringify(signUpData));
      window.location.assign(
        `/user-thankyou?firstName=${encodeURIComponent(first_name)}`
      );
    } catch (error) {
      setErrorMessage(
        getApiErrorMessage(error, "An error occurred. Please try again.")
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const isVendor = formData.user_type === "vendor";

  const lockFirstName = Boolean(
    activeVerifiedAccount?.accountExists &&
      activeVerifiedAccount?.firstName
  );

  const lockLastName = Boolean(
    activeVerifiedAccount?.accountExists &&
      activeVerifiedAccount?.lastName
  );

  const lockEmail = Boolean(activeVerifiedAccount?.email);

  const signedInName =
    [activeVerifiedAccount?.firstName, activeVerifiedAccount?.lastName]
      .filter(Boolean)
      .join(" ") || "GoOurHub member";

  return (
    <div className="ps-shell">
      <Header />

      <main className="ps-page">
        <div className="ps-layout">
          {/* ---------- LEFT: pitch ---------- */}
          <aside className="ps-pitch">
            <span className="ps-kicker">Partner with GoOurHub</span>
            <h1>
              Put your business in front of your community
              <span className="ps-accent">.</span>
            </h1>
            <p className="ps-lede">
              Join local vendors on the Hub and HubMart. Sign-up takes a few
              minutes your branded page and listing come with it.
            </p>

            {/* Premium clarity note */}
            <div className="ps-premium-note">
              <span className="ps-premium-note__badge">Plans</span>
              <p>
                Vendors can choose a <strong>paid premium</strong> listing  
                like placing your ad on GoOurHub or a basic{" "}
                <strong>free listing</strong>. Signing up as a User is always
                free.
              </p>
            </div>

            <ul className="ps-features">
              {features.map((feature) => (
                <li key={feature.name} className="ps-feature">
                  <span className="ps-feature__icon" aria-hidden="true">
                    {feature.icon}
                  </span>
                  <span>{feature.name}</span>
                  <FaCheckCircle className="ps-feature__check" aria-hidden="true" />
                </li>
              ))}
            </ul>

            <div className="ps-links">
              <Link to="/faq" className="ps-link-card">
                <FaQuestionCircle aria-hidden="true" />
                <span>
                  <strong>Need help?</strong>
                  Read our FAQs
                </span>
                <FaArrowRight className="ps-link-card__arrow" aria-hidden="true" />
              </Link>

              <Link to="/Pricing" className="ps-link-card">
                <FaTag aria-hidden="true" />
                <span>
                  <strong>Pricing</strong>
                  See what it costs
                </span>
                <FaArrowRight className="ps-link-card__arrow" aria-hidden="true" />
              </Link>
            </div>
          </aside>

          {/* ---------- RIGHT: form ---------- */}
          <section className="ps-card">
            <div className="ps-card__head">
              <span className="ps-kicker">Sign up</span>
              <h2>Add Business</h2>
            </div>

            {errorMessage && (
              <MyErrorModal
                message={errorMessage}
                onClose={() => setErrorMessage("")}
              />
            )}
            {successMessage && <p className="ps-success">{successMessage}</p>}
{hasReusableVerification && activeVerifiedAccount?.email && (
  <div
    className="ps-account-status"
    role="status"
  >
    <div>
      <strong>
        ✓ Signed in as {signedInName}
      </strong>

      <span>
        {activeVerifiedAccount.email}
        {" · "}
        Verified GoOurHub account
      </span>
    </div>
  </div>
)}
            <form id="joinForm" className="ps-form" onSubmit={handleSubmit}>
              <div className="ps-field">
                <label htmlFor="ps-user-type">I am </label>
                <select
                  id="ps-user-type"
                  name="requester_role"
                  value={formData.requester_role}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select one</option>
                  <option value="owner">Business Owner</option>
                  <option value="team_member">Team Member/Employee</option>
                </select>
              </div>

              <div className="ps-grid">
                <div className="ps-field">
                  <label htmlFor="ps-first-name">First name</label>
                  <input
  id="ps-first-name"
  type="text"
  name="first_name"
  value={formData.first_name}
  onChange={handleChange}
  readOnly={lockFirstName}
  aria-readonly={lockFirstName}
  required
/>
                </div>

                <div className="ps-field">
                  <label htmlFor="ps-last-name">Last name</label>
                  <input
  id="ps-last-name"
  type="text"
  name="last_name"
  value={formData.last_name}
  onChange={handleChange}
  readOnly={lockLastName}
  aria-readonly={lockLastName}
  required
/>
                </div>
              </div>

              <div className="ps-grid">
                <div className="ps-field">
<label htmlFor="ps-email">
  {lockEmail ? "Signed-in email" : "Email"}
</label>

<input
  id="ps-email"
  name="email"
  type="email"
  value={formData.email}
  onChange={handleChange}
  readOnly={lockEmail}
  aria-readonly={lockEmail}
  ref={emailRef}
  aria-label={lockEmail ? "Signed-in account email" : "Email address"}
  required
/>

                  {isVendor &&
  !hasReusableVerification && (
    <div className="ps-email-verification">
                      {verificationStage === "idle" && (
                        <button
                          type="button"
                          className="ps-btn ps-btn--ghost ps-btn--compact"
                          onClick={sendVerificationCode}
                          disabled={isVerifyingEmail || !formData.email}
                        >
                          {isVerifyingEmail
                            ? "Sending…"
                            : "Send verification code"}
                        </button>
                      )}

                      
                      {verificationStage === "verified" && (
                        <div className="ps-verified-message" role="status">
                          <FaCheckCircle aria-hidden="true" />
                          <span>
                            {accountExists
                              ? "Verified existing GoOurHub account"
                              : "Email verified"}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="ps-field">
                  <label htmlFor="ps-phone">Phone number</label>
                  <input
                    id="ps-phone"
                    type="tel"
                    name="ph_no"
                    placeholder="10 digits"
                    value={formData.ph_no}
                    onChange={handleChange}
                    ref={phoneRef}
                    required
                  />
                </div>
              </div>

              {isVendor && (
                <div className="ps-section">
                  <h3 className="ps-section__title">Business details</h3>

                  {/* ---- Plan selector: premium (Stripe) vs free listing ---- */}
                  <div
                    className="ps-plan-toggle"
                    role="radiogroup"
                    aria-label="Choose your listing plan"
                  >
                    <button
                      type="button"
                      role="radio"
                      aria-checked={listingPlan === "premium"}
                      className={`ps-plan-card ps-plan-card--premium ${
                        listingPlan === "premium" ? "is-active" : ""
                      }`}
                      onClick={() => setListingPlan("premium")}
                    >
                      <span className="ps-plan-card__badge">Premium</span>
                      <span className="ps-plan-card__title">Paid premium</span>
                      <span className="ps-plan-card__desc">
                        Branded page, promotion &amp; full listing. Checkout
                        after sign-up.
                      </span>
                      <FaCheckCircle
                        className="ps-plan-card__check"
                        aria-hidden="true"
                      />
                    </button>

                    <button
                      type="button"
                      role="radio"
                      aria-checked={listingPlan === "free"}
                      className={`ps-plan-card ${
                        listingPlan === "free" ? "is-active" : ""
                      }`}
                      onClick={() => setListingPlan("free")}
                    >
                      <span className="ps-plan-card__badge ps-plan-card__badge--free">
                        Free
                      </span>
                      <span className="ps-plan-card__title">Free listing</span>
                      <span className="ps-plan-card__desc">
                        Basic listing on GoOurHub. No payment needed.
                      </span>
                      <FaCheckCircle
                        className="ps-plan-card__check"
                        aria-hidden="true"
                      />
                    </button>
                  </div>

                  <div className="ps-field">
                    <label htmlFor="ps-bus-name">Business name</label>
                    <input
                      id="ps-bus-name"
                      type="text"
                      name="bus_name"
                      value={formData.bus_name}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="ps-field">
                    <label htmlFor="ps-street-address">Street address</label>
                    <input
                      id="ps-street-address"
                      type="text"
                      name="street_address"
                      value={formData.street_address}
                      onChange={handleChange}
                      placeholder="123 Main Street"
                      required
                    />
                  </div>

                  <div className="ps-field">
              
                    <div className="ps-field">
  <label htmlFor="ps-bus-type">Business type</label>

  <BusinessCategorySelect
    id="ps-bus-type"
    name="business_type"
    value={formData.business_type}
    onChange={handleChange}
    required
  />
</div>
                  </div>

                  {(formData.business_type === "others" ||
                    formData.business_type === "multiple") && (
                    <div className="ps-field">
                      <label htmlFor="ps-other-bus-type">
                        Specify your business type
                      </label>
                      <input
                        id="ps-other-bus-type"
                        type="text"
                        name="other_business_type"
                        placeholder="Please specify your business type"
                        value={formData.other_business_type}
                        onChange={handleChange}
                        required
                      />
                    </div>
                  )}

                  <div className="ps-field">
                    <label>
                      Community focus{" "}
                      <span className="ps-optional">(optional)</span>
                    </label>
                    <p className="ps-hint">
                      If your business focuses on a particular community&rsquo;s
                      goods, services, or culture, select it here.
                    </p>
                    <Select
                      className="ps-select"
                      classNamePrefix="ps-select"
                      isMulti
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      menuShouldScrollIntoView={false}
                      options={communityOptions}
                      onChange={(selected) =>
                        handleMultiChange(selected, "communities")
                      }
                      value={formData.communities}
                      placeholder="Select communities…"
                    />
                    {formData.communities.some((c) => c.value === "Others") && (
                      <input
                        type="text"
                        name="other_community"
                        placeholder="Please specify other community"
                        value={formData.other_community}
                        onChange={handleTextChange}
                        style={{ marginTop: 10 }}
                      />
                    )}
                  </div>

                  <div className="ps-field">
                    <label>
                      Languages besides English{" "}
                      <span className="ps-optional">(optional)</span>
                    </label>
                    <p className="ps-hint">
                      What languages do you speak or offer services in? Select
                      all that apply.
                    </p>
                    <Select
                      className="ps-select"
                      classNamePrefix="ps-select"
                      isMulti
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      menuShouldScrollIntoView={false}
                      options={languageOptions}
                      onChange={(selected) =>
                        handleMultiChange(selected, "languages")
                      }
                      value={formData.languages}
                      placeholder="Select languages…"
                    />
                    {formData.languages.some((l) => l.value === "others") && (
                      <input
                        type="text"
                        name="other_language"
                        placeholder="Please specify other language(s)"
                        value={formData.other_language}
                        onChange={handleTextChange}
                        style={{ marginTop: 10 }}
                      />
                    )}
                  </div>

                  <div className="ps-field ps-photo-field">
                    <div className="ps-photo-heading">
                      <div>
                        <label htmlFor="ps-business-photos">
                          Business photos{" "}
                          <span className="ps-optional">
                            (optional)
                          </span>
                        </label>
                        <p className="ps-hint">
                          Add up to 4 photos of your business. The first
                          photo will be used as the cover photo.
                        </p>
                      </div>

                      <span className="ps-photo-count">
                        {businessPhotos.length}/{MAX_BUSINESS_PHOTOS}
                      </span>
                    </div>

                    <input
                      ref={businessPhotoInputRef}
                      id="ps-business-photos"
                      className="ps-photo-input"
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      multiple
                      onChange={handleBusinessPhotoSelection}
                      disabled={isSubmitting}
                    />

                    <div className="ps-photo-grid">
                      {businessPhotos.map((photo, index) => (
                        <div
                          className="ps-photo-preview"
                          key={photo.id}
                        >
                          <img
                            src={photo.previewUrl}
                            alt={`Business photo preview ${index + 1}`}
                          />

                          {index === 0 && (
                            <span className="ps-photo-cover">
                              Cover
                            </span>
                          )}

                          <button
                            type="button"
                            className="ps-photo-remove"
                            onClick={() =>
                              removeBusinessPhoto(photo.id)
                            }
                            aria-label={`Remove business photo ${
                              index + 1
                            }`}
                            disabled={isSubmitting}
                          >
                            <FaTimes aria-hidden="true" />
                          </button>
                        </div>
                      ))}

                      {businessPhotos.length <
                        MAX_BUSINESS_PHOTOS && (
                        <button
                          type="button"
                          className="ps-photo-add"
                          onClick={openBusinessPhotoPicker}
                          disabled={isSubmitting}
                        >
                          {businessPhotos.length ? (
                            <FaPlus aria-hidden="true" />
                          ) : (
                            <FaImage aria-hidden="true" />
                          )}
                          <span>
                            {businessPhotos.length
                              ? "Add another"
                              : "Add photos"}
                          </span>
                          <small>
                            JPG, PNG or WEBP
                            <br />
                            Max 5 MB each
                          </small>
                        </button>
                      )}
                    </div>

                    {photoWarning && (
                      <p
                        className="ps-photo-warning"
                        role="status"
                      >
                        {photoWarning}
                      </p>
                    )}

                    <p className="ps-photo-note">
                      Photos are optional. You can submit the listing
                      without adding any pictures.
                    </p>
                  </div>
                </div>
              )}

              <div className="ps-grid">
                <div className="ps-field">
                  <label htmlFor="ps-city">City</label>
                  <select
                    id="ps-city"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    required
                  >
                    <option value="San Antonio">San Antonio</option>
                  </select>
                </div>

                <div className="ps-field">
                  <label htmlFor="ps-state">State</label>
                  <select
                    id="ps-state"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    required
                  >
                    <option value="Texas">Texas</option>
                  </select>
                </div>
              </div>

              <p className="ps-note">
                Your email must be verified before a vendor listing is submitted.
                {isVendor && listingPlan === "premium" && (
                  <>
                    {" "}
                    Premium vendors are redirected to secure checkout to
                    complete their listing payment.
                  </>
                )}
                {isVendor && listingPlan === "free" && (
                  <>
                    {" "}
                    Free listings skip checkout you&rsquo;ll go straight to
                    your confirmation page.
                  </>
                )}
              </p>

              <label className="ps-terms">
                <input
                  type="checkbox"
                  name="isAccepted"
                  checked={formData.isAccepted}
                  onChange={handleChange}
                />
                <span>
                  I have read the{" "}
                  <Link to="/terms" className="ps-terms__link">
                    terms
                  </Link>{" "}
                  and agree to move forward.
                </span>
              </label>

              <button
                type="submit"
                className="ps-btn ps-btn--submit"
                disabled={
                  !formData.isAccepted ||
                  isSubmitting ||
                  (
  isVendor &&
  !(
    verificationStage === "verified" &&
    (verificationToken || sessionToken)
  )
)
                }
              >
                {isUploadingPhotos
                  ? "Uploading photos…"
                  : isSubmitting
                    ? "Processing…"
                    : isVendor && listingPlan === "premium"
                      ? "Continue to secure payment"
                      : isVendor
                        ? "Submit free listing"
                        : "Sign Up"}
                {!isSubmitting && <FaArrowRight aria-hidden="true" />}
              </button>
            </form>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}

export default PartnerSignUpPage;
